import cx from "classnames";
import { cloneDeep, isEqual } from "lodash";
import { DragEvent, FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ColumnFilter } from ".";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig } from "@app/shared/waffles/exports";
import { WafflesFilterGroup, WafflesFilterLogic, WafflesFilterType } from "@app/shared/waffles/filterExports";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";

interface WafflesFilterProps {
  columns: ColumnConfig;
  disableProcessing?: boolean;
  initialFilters?: WafflesFilterGroup[];
  onAnyChange?: (hasChanges: boolean) => void;
  onFilterSubmit: (filters: WafflesFilterGroup[]) => void;
  onClose?: () => void;
  onColumnSelect?: (column: string) => void;
  onOpen?: () => void;
  readonly?: boolean;
  usedColumns: string[];
}

let draggedFilter: HTMLDivElement = null;

const WafflesFilterSystem: FC<React.PropsWithChildren<WafflesFilterProps>> = (props: WafflesFilterProps) => {
  const [filterableColumns, setFilterableColumns] = useState(getFilterableColumns(props.columns, props.usedColumns));
  const [filters, setFilters] = useState<WafflesFilterGroup[]>(props.initialFilters || []);

  const [simpleFilterColumn, setSimpleFilterColumn] = useState("");
  const [simpleFilterType, setSimpleFilterType] = useState<WafflesFilterType>();
  const [simpleFilterOperator, setSimpleFilterOperator] = useState("");
  const [simpleFilterOperatorDisplayValue, setSimpleFilterOperatorDisplayValue] = useState("");
  const [simpleFilterValue, setSimpleFilterValue] = useState<string | boolean>("");
  const [simpleFilterGroup, setSimpleFilterGroup] = useState<number>(1);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFiltered, setIsFiltered] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastRunFilters, setLastRunFilters] = useState([]);
  const fieldsetRef = useRef<HTMLFieldSetElement>(null);
  const accordionClassRef = useRef<string>(null);

  useEffect(() => {
    const newFilterableColumns = getFilterableColumns(props.columns, props.usedColumns);
    setFilterableColumns(newFilterableColumns);
  }, [props.columns, props.usedColumns]);

  useEffect(() => {
    setHasChanges(!isEqual(filters, lastRunFilters));
  }, [filters]);

  useEffect(() => {
    if (!!props.onAnyChange) {
      props.onAnyChange(hasChanges);
    }
  }, [hasChanges]);

  useEffect(() => {
    // we need to pass separate instances of props.initialFilters here
    // otherwise updating just a logic value will update both and won't trigger hasChanged
    if (props.initialFilters) {
      setFilters(cloneDeep(props.initialFilters));
      setLastRunFilters(cloneDeep(props.initialFilters));
      setIsFiltered(props.initialFilters.length > 0);
    }
  }, [props.initialFilters]);

  useEffect(() => {
    const accordionObserver = new MutationObserver((e) => {
      const classList = (e[0].target as HTMLElement).classList;
      // bootstrap's collapse mechanism relies on the "collapse" class,
      // adds the "collapsing" class when opening or closing, and
      // adds the "show" class when finished opening. So it triggers this observer multiple times
      // on click, and we only want to fire the functions when it's done opening or closing
      if (!classList.contains("collapsing") && classList.toString() !== accordionClassRef.current) {
        accordionClassRef.current = classList.toString();
        if (classList.contains("show") && !!props.onOpen) {
          props.onOpen();
        } else if (!!props.onClose) {
          props.onClose();
        }
      }
    });
    if (fieldsetRef.current) {
      accordionObserver.observe(fieldsetRef.current, {
        attributeFilter: ["class"],
        attributes: true,
      });
    }
  }, [fieldsetRef.current]);

  useEffect(() => {
    if (!!props.onColumnSelect) {
      props.onColumnSelect(simpleFilterColumn);
    }
  }, [simpleFilterColumn]);

  const removeFilter = (groupIndex: number, columnIndex: number) => {
    const updatedFilters = [...filters];
    updatedFilters[groupIndex].cols.splice(columnIndex, 1);
    if (updatedFilters[groupIndex].cols.length === 0) {
      updatedFilters.splice(groupIndex, 1);
    }
    setFilters(updatedFilters);
  };

  const renderOperatorOptions = () => {
    if (simpleFilterType === undefined) {
      return;
    }
    switch (simpleFilterType) {
      case WafflesFilterType.string:
        return [
          <option key="equal to" value="equal to">equal to</option>,
          <option key="contains" value="contains">contains</option>,
          <option key="does not contain" value="does not contain">does not contain</option>,
        ];
      case WafflesFilterType.number:
        return [
          <option key="greater than" value="greater than">greater than</option>,
          <option key="equal to" value="equal to">equal to</option>,
          <option key="less than" value="less than">less than</option>,
        ];
      case WafflesFilterType.boolean:
        return [
          <option key="yes" value="yes">yes</option>,
          <option key="no" value="no">no</option>,
        ];
      case WafflesFilterType.dateTime:
        return [
          <option key="greater than" value="greater than">after</option>,
          <option key="equal to" value="equal to">equal to</option>,
          <option key="less than" value="less than">before</option>,
        ];
      default:
        return [];
    }
  };

  const updateGroupInternalLogic = (logic: string, groupIndex: number) => {
    const updatedFilters = [...filters];
    updatedFilters[groupIndex].logicInternal = logic === WafflesFilterLogic.And ? WafflesFilterLogic.And : WafflesFilterLogic.Or;
    setFilters(updatedFilters);
  };

  const updateIntergroupLogic = (logic: string, groupIndex: number) => {
    const updatedFilters = [...filters];
    updatedFilters[groupIndex].logicPrevGroup = logic === WafflesFilterLogic.And ? WafflesFilterLogic.And : WafflesFilterLogic.Or;
    setFilters(updatedFilters);
  };

  const updateSimpleFilterColumn = (columnKey: string) => {
    setSimpleFilterColumn(columnKey);
    const filterType = Object.entries(filterableColumns).find((fc) => fc[0] === columnKey)[1].filterType;
    setSimpleFilterType(filterType);
    if (filterType === WafflesFilterType.boolean) {
      setSimpleFilterValue(false);
      setSimpleFilterOperatorDisplayValue("is false");
    }
  };

  const saveSimpleFilter = () => {
    // This error shouldn't be possible to get to without editing the button, but here it is
    if (!simpleFilterColumn || (!simpleFilterOperator && simpleFilterType !== WafflesFilterType.boolean) || (simpleFilterValue === undefined || simpleFilterValue === null || simpleFilterValue === "")) {
      setErrorMessage("Every field needs to be populated.");
      return;
    }
    const updatedFilters = [...filters];
    const columnConfig = Object.entries(filterableColumns).find((fc) => fc[0] === simpleFilterColumn);
    const newColumnFilter: ColumnFilter = {
      displayName: columnConfig[1].filterText || columnConfig[1].displayText,
      key: simpleFilterColumn,
      type: simpleFilterType,
      value: {
        operator: simpleFilterOperator,
        operatorDisplayValue: simpleFilterOperatorDisplayValue,
        value: simpleFilterValue,
      },
    };

    if (filters.length) {
      // It shouldn't be possible to add a filter to a group that doesn't exist, i.e. outside the acceptable bounds of updatedFilters
      updatedFilters[simpleFilterGroup - 1].cols.push(newColumnFilter);
    } else {
      const newWafflesFilters: WafflesFilterGroup = {
        cols: [newColumnFilter],
        logicInternal: WafflesFilterLogic.And,
        logicPrevGroup: WafflesFilterLogic.And,
      };
      updatedFilters.push(newWafflesFilters);
    }
    setFilters(updatedFilters);
    setSimpleFilterColumn("");
    setSimpleFilterOperatorDisplayValue("");
    setSimpleFilterOperator("");
    setSimpleFilterType(undefined);
    setSimpleFilterValue("");
    // I assume the user will want to add multiple filters to the selected group so I won't reset it to 1 here
    setErrorMessage("");
  };

  const addFilterGroup = () => {
    // need to clone deep so JS actually recognizes it as a new object
    const newFilters = cloneDeep(filters);
    const newGroup: WafflesFilterGroup = {
      cols: [],
      logicInternal: WafflesFilterLogic.Or,
      logicPrevGroup: WafflesFilterLogic.And,
    };
    newFilters.push(newGroup);
    setFilters(newFilters);
  };

  const deleteFilterGroup = (index: number) => {
    const newFilters = cloneDeep(filters);
    newFilters.splice(index, 1);
    setFilters(newFilters);
  };

  const renderFilterGroupOptions = useMemo(() => {
    if (filters.length === 0) {
      return [<option value="1" key="1">1</option>];
    }
    return filters.map((f, index) => <option value={index + 1} key={index + 1}>{index + 1}</option>);
  }, [filters]);

  const runFilters = () => {
    setIsFiltered(filters.length > 0);
    // this needs to be cloneDeep, since filters is nested objects. spread operator only does a shallow copy, and the children are passed by reference
    setLastRunFilters(cloneDeep(filters));
    setHasChanges(false);
    props.onFilterSubmit(filters);
  };

  // for drag and drop, we can't rely on state to toggle these classes because updating state and rerendering the component triggers dragEnd
  const handleDragStart = (e: DragEvent<HTMLSpanElement>) => {
    draggedFilter = e.target as HTMLDivElement;
    [...fieldsetRef.current.querySelectorAll(".waffles-filters__filter-group")].filter((item) => item.getAttribute("data-group-id") !== (e.target as HTMLElement).closest(".waffles-filters__filter-group").getAttribute("data-group-id")).forEach((item) => item.classList.add("dropspot"));
  };

  const handleDragEnd = () => {
    [...fieldsetRef.current.querySelectorAll(".waffles-filters__filter-group")].forEach((item) => item.classList.remove("dropspot"));
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const dropspot = (e.target as HTMLElement).closest(".dropspot");
    if (dropspot) {
      dropspot.classList.add("hovered");
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    const dropspot = (e.target as HTMLElement).closest(".dropspot");
    if (dropspot) {
      dropspot.classList.remove("hovered");
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    handleDragLeave(e);
    if (!(e.target as HTMLElement).closest(".dropspot")) {
      return;
    }

    const draggedGroupId = parseInt(draggedFilter.getAttribute("data-group-id"), 10);
    const draggedFilterId = parseInt(draggedFilter.getAttribute("data-filter-id"), 10);
    const newGroupId = parseInt((e.target as HTMLElement).closest(".dropspot").getAttribute("data-group-id"), 10);
    const newFilters = cloneDeep(filters);

    const removedFilter = newFilters[draggedGroupId].cols.splice(draggedFilterId, 1);
    newFilters[newGroupId].cols.push(removedFilter[0]);

    if (newFilters[draggedGroupId].cols.length === 0) {
      newFilters.splice(draggedGroupId, 1);
    }

    handleDragEnd();
    setFilters(newFilters);

    draggedFilter = null;
  };

  //const getTotalFiltersLength = () => {
  //  let total = 0;
  //  filters.forEach((filterGroup) => {
  //    filterGroup.cols.forEach(() => {
  //      total += 1;
  //    });
  //  });
  //  return total;
  //};

  const getFiltersString = useMemo(() => {
    if (props.readonly) {
      return "(readonly mode)";
    } else if (isFiltered && hasChanges) {
      return "(currently applied, and has unapplied changes)";
    } else if (isFiltered && !hasChanges) {
      return "(currently applied)";
    } else if (!isFiltered && hasChanges) {
      return "(has unapplied changes)";
    }
    return "";
  }, [isFiltered, hasChanges, props.readonly]);

  return (
    <>
      <QESButton qesType="secondary" className="accordion-expansion-button text-dark fw-normal text-decoration-none" data-bs-toggle="collapse" data-bs-target="#projectchange" aria-expanded={accordionClassRef.current?.includes("open")} aria-pressed={accordionClassRef.current?.includes("open")} aria-controls="projectchange" qesTestId="waffles-filters__toggle">
        <span className="collapse-indicator me-3"><i className="fa fa-fw fa-chevron-right" /></span>
        Filters {getFiltersString}
      </QESButton>
      <div className="collapse show" id="projectchange">
        <fieldset className="waffles-filters-fieldset" ref={fieldsetRef}>
          <div className="row text-danger">{errorMessage}</div>
          <div className="row align-items-center mb-3">
            <div className="col-3 form-group mb-0">
              <QESSelect
                disabled={props.readonly}
                label="Column"
                value={simpleFilterColumn}
                onChange={(e) => updateSimpleFilterColumn(e.target.value)}
                testId="waffles-filters__column-dropdown"
              >
                {Object.entries(filterableColumns).map((fc) => <option key={fc[0]} value={fc[0]}>{fc[1].filterText || fc[1].displayText}</option>)}
              </QESSelect>

            </div>
            <div
              className={cx({
                "col-1 col-lg-2": simpleFilterType === WafflesFilterType.dateTime,
                "col-2 col-lg-3": simpleFilterType !== WafflesFilterType.dateTime,
                "form-group mb-0": true,
                "hidden": !simpleFilterColumn || simpleFilterType === WafflesFilterType.boolean || props.readonly,
              })}
            >
              <QESSelect
                label="Operator"
                value={simpleFilterOperator}
                onChange={(e) => {
                  setSimpleFilterOperator(e.target.value);
                  setSimpleFilterOperatorDisplayValue(e.target.options[e.target.selectedIndex].text);
                }}
                disabled={simpleFilterColumn === ""}
                testId="waffles-filters__operator-dropdown"
              >
                {renderOperatorOptions()}
              </QESSelect>
            </div>
            <div
              className={cx({
                "col-2": simpleFilterType !== WafflesFilterType.dateTime,
                "col-3": simpleFilterType === WafflesFilterType.dateTime,
                "form-group mb-0": true,
                "hidden": !simpleFilterColumn || props.readonly,
              })}
            >
              {simpleFilterType === WafflesFilterType.boolean ? (
                <>
                  <QESCheckbox
                    label="Value"
                    className="d-block mt-auto mb-auto"
                    checked={simpleFilterValue as boolean}
                    onChange={(e) => {
                      setSimpleFilterValue(e.target.checked);
                      setSimpleFilterOperatorDisplayValue(`is ${e.target.checked.toString()}`);
                    }}
                    id="value"
                    testId="waffles-filters__value"
                  />
                </>
              ) : simpleFilterType === WafflesFilterType.dateTime ? (
                <QESDateInput
                  label="Value"
                  value={simpleFilterValue as string}
                  onChange={(e) => setSimpleFilterValue(e.target.value)}
                  id="value"
                  testId="waffles-filters__column-value"
                />
              ) : (
                <QESTextInput
                  label="Value"
                  value={simpleFilterValue as string}
                  onChange={(e) => setSimpleFilterValue(e.target.value)}
                  id="value"
                  testId="waffles-filters__column-value"
                />
              )}
            </div>
            {filters.length >= 1 && filters[0].cols.length >= 1 && (<div
              className={cx({
                "col-1 form-group mb-0": true,
              })}
            >
              <QESSelect
                disabled={props.readonly}
                label="Group"
                value={simpleFilterGroup}
                onChange={(e) => setSimpleFilterGroup(parseInt(e.target.value, 10))}
                testId="waffles-filter__group-number-dropdown"
              >
                {renderFilterGroupOptions}
              </QESSelect>
            </div>
            )}
            <div
              className={cx({
                "col-1 waffles-filters__simple-filter-button ps-0 pe-0 text-center": true,
                "offset-1": simpleFilterColumn && filters.length === 0, // if there's no groups dropdown yet
                "offset-3": simpleFilterColumn && simpleFilterType === WafflesFilterType.boolean && filters.length !== 0, // if there is a column selected and a groups dropdown
                "offset-4": simpleFilterColumn && simpleFilterType === WafflesFilterType.boolean && filters.length === 0, // if there is a column selected and no groups dropdown
                "offset-5": !simpleFilterColumn && filters.length !== 0, // if there's no filter selected but is a groups dropdown
                "offset-6": !simpleFilterColumn && filters.length === 0, // if there's no filter selected and no groups dropdown yet
              })}
            >
              <QESButton qesType="secondary" onClick={saveSimpleFilter} disabled={!simpleFilterColumn || (!simpleFilterOperator && simpleFilterType !== WafflesFilterType.boolean) || (simpleFilterValue === undefined || simpleFilterValue === null || simpleFilterValue === "") || props.readonly} qesTestId="waffles-filters__add" className="h-auto">
                Add Filter
              </QESButton>
            </div>
            <div className="col-1 waffles-filters__simple-filter-button ps-0 pe-0 text-center">
              <QESButton qesType="secondary" onClick={addFilterGroup} qesTestId="waffles-filters__advanced" disabled={filters.length === 0 || filters[filters.length - 1].cols.length === 0 || props.readonly} className="h-auto">
                Add Group
              </QESButton>
            </div>
            <div className="col-1 waffles-filters__simple-filter-button ps-0 pe-0 text-center">
              {/* Disabled:
                * If there are no changes to process
                * OR the parent component has disabled it for any reason
                * OR we're in readonly mode
                */}
              <QESButton qesType="primary" onClick={runFilters} disabled={!hasChanges || props.disableProcessing || props.readonly} qesTestId="waffles-filters__apply" className="h-auto">
                Process
              </QESButton>
            </div>
          </div>
          {
            filters.map((filterGroup, groupIndex) => (
              <Fragment key={groupIndex}>
                {groupIndex > 0 && (
                  <div className="form-group col-2">
                    <QESSelect
                      hideLabel={true}
                      disabled={props.readonly}
                      label="Intergroup Logic"
                      onChange={(e) => updateIntergroupLogic(e.target.value, groupIndex)}
                      value={filters[groupIndex].logicPrevGroup}
                      testId={`waffles-filters__group-${groupIndex}-intergroup-logic-dropdown`}
                    >
                      {[
                        <option key={WafflesFilterLogic.And} value={WafflesFilterLogic.And}>And</option>,
                        <option key={WafflesFilterLogic.Or} value={WafflesFilterLogic.Or}>Or</option>,
                      ]}
                    </QESSelect>

                  </div>
                )}
                <div
                  className="row form-group waffles-filters__filter-group"
                  data-group-id={groupIndex}
                  onDragEnter={(e) => handleDragOver(e)}
                  onDragOver={(e) => handleDragOver(e)}
                  onDragLeave={(e) => handleDragLeave(e)}
                  onDrop={(e) => handleDrop(e)}
                >
                  <div className="col-12">
                    <span className="demibold d-inline-block me-3 waffles-filters__group-title position-relative" data-disabled={props.readonly} onClick={() => { if (!props.readonly) { deleteFilterGroup(groupIndex); } }}>
                      <i className="fa fa-trash text-danger position-absolute waffles-filters__delete-group-icon" />
                      Group {groupIndex + 1}:
                    </span>
                    {filterGroup.cols.map((col, columnIndex) => {
                      const filterValueText = col.type === WafflesFilterType.dateTime ?
                        new Date(col.value.value as string).toLocaleString("default", { day: "numeric", month: "long", year: "numeric" }) :
                        col.value.value;
                      return (
                        <Fragment key={columnIndex}>
                          {columnIndex === 1 && (
                            <div className="d-inline-block ms-3">
                              <QESSelect
                                disabled={props.readonly}
                                hideLabel={true}
                                label="Group Logic"
                                containerClassName="mb-1 mt-1"
                                onChange={(e) => updateGroupInternalLogic(e.target.value, groupIndex)}
                                value={filters[groupIndex].logicInternal}
                                testId={`waffles-filters__group-${groupIndex}-logic-dropdown`}
                              >
                                {[
                                  <option key={WafflesFilterLogic.And} value={WafflesFilterLogic.And}>And</option>,
                                  <option key={WafflesFilterLogic.Or} value={WafflesFilterLogic.Or}>Or</option>,
                                ]}
                              </QESSelect>
                            </div>
                          )}
                          {columnIndex > 1 && <span className="ms-3 mt-auto mb-auto">{filterGroup.logicInternal}</span>}
                          <span
                            key={columnIndex}
                            className={`me-0${columnIndex > 0 ? " ms-3" : ""} waffles-filters__filter d-inline-block`}
                            onDragStart={(e) => handleDragStart(e)}
                            onDragEnd={handleDragEnd}
                            data-testid={`waffles-filters__group-${groupIndex}-filter-${columnIndex}`}
                            draggable={!props.readonly}
                            data-group-id={groupIndex}
                            data-filter-id={columnIndex}
                          >
                            {col.displayName} {col.value.operatorDisplayValue || col.value.operator} {filterValueText} <i className="fa fa-times-circle ms-2" data-disabled={props.readonly} onClick={(e) => { e.stopPropagation(); if (!props.readonly) { removeFilter(groupIndex, columnIndex); } }} />
                          </span>
                        </Fragment>
                      );
                    })}
                  </div>
                </div>
              </Fragment>
            ))
          }
        </fieldset>
      </div>
    </>
  );
};

const getFilterableColumns = (allColumns: ColumnConfig, usedColumns: string[]) => {
  const filteredEntries = Object.entries(allColumns)
    .filter((colConfig) => colConfig[1].displayText.length > 0 && !colConfig[1].noFilter && usedColumns.includes(colConfig[0]))
    .sort((a, b) => a[0] > b[0] ? 1 : -1);
  const newConfig: ColumnConfig = {};
  filteredEntries.forEach((fe) => newConfig[fe[0]] = fe[1]);
  return newConfig;
};

export default WafflesFilterSystem;
